/**
 * editor2/hooks/useSquareInvoice.ts
 * Square インボイスの作成・取り消しフック
 * ReservationEditorPanel の handleCreateInvoice / handleVoidInvoice を抽出
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import type { EditorReservation } from '../types';
import { SYS_INVOICE_BASE } from '../../../constants/calendar';
import { isSquareEnabled, getSquareIndex, canOperateSquareIndex } from '../../../config/backend';
import { SQUARE_LOCATION_TOKENS_BY_INDEX } from '../../../config/instances';
import { buildSquareInvoicePayload } from '../../../lib/squareInvoicePayload';
import {
  baseOnlyCandidate,
  buildBaseOnlyConfirmMessage,
  buildBaseOnlyMemoLine,
  appendBaseOnlyMemo,
  type InvoiceSumMismatch,
} from '../../../lib/invoiceBaseOnly';

interface UseSquareInvoiceOptions {
  getData: () => EditorReservation;
  onChange?: (updates: Partial<EditorReservation>) => void;
  onSave?: (data: EditorReservation, options?: { keepOpen?: boolean }) => Promise<void>;
  sysApiBase: string;
}

// sqNum(index) → Square location token の解決は instances.ts(SSOT)に集約。
const SQUARE_LOCATION_TOKENS = SQUARE_LOCATION_TOKENS_BY_INDEX;

/**
 * Parse invoice_link to extract actual ID and location token
 * Supports sq:0:xxx / sq:1:xxx / sq:2:xxx prefix format
 *
 * sqNum: prefix から解決した Square 設定番号。prefix 無しの旧データは 0(那覇)
 *   — server 側 square_void_invoice.php の既定と同じ扱い。
 * isPaymentUrl: `https://square.link/u/...` 形式の決済リンク。Square の invoice ID では
 *   ないため取り消し API に投げても失敗する(宮古の移行データに 119 件存在)。
 */
export function parseInvoiceLink(rawValue: string): {
  actualId: string;
  locationToken: string;
  sqNum: number;
  isPaymentUrl: boolean;
} {
  let actualId = rawValue;
  let locationToken = SQUARE_LOCATION_TOKENS['0']; // default (Naha: 0)
  let sqNum = 0;

  if (rawValue.startsWith('sq:')) {
    const parts = rawValue.split(':');
    const sqIndex = parts[1];
    // Invoice ID itself may contain colons, so join from index 2 onwards
    actualId = parts.slice(2).join(':');

    if (SQUARE_LOCATION_TOKENS[sqIndex]) {
      locationToken = SQUARE_LOCATION_TOKENS[sqIndex];
    }
    if (/^\d+$/.test(sqIndex)) {
      sqNum = parseInt(sqIndex, 10);
    }
  } else if (rawValue.includes('sq:')) {
    // Legacy fallback
    const parts = rawValue.split(':');
    actualId = parts[parts.length - 1];
  }

  return { actualId, locationToken, sqNum, isPaymentUrl: /^https?:\/\//i.test(rawValue) };
}

export default function useSquareInvoice({
  getData,
  onChange,
  onSave,
  sysApiBase,
}: UseSquareInvoiceOptions) {
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [isVoidingInvoice, setIsVoidingInvoice] = useState(false);

  // Keep latest refs for async callbacks
  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  const getDataRef = useRef(getData);
  useEffect(() => {
    getDataRef.current = getData;
  }, [getData]);

  const createInvoice = useCallback(
    async (isDirty: boolean) => {
      // 宮古など Square 未設定インスタンスでは発行禁止
      // （sqNum 未指定の発行は那覇 location に作られてしまうため）
      if (!isSquareEnabled()) {
        window.alert('この店舗では Square 請求書機能が未設定のため発行できません。');
        return;
      }
      const safe = getData();

      if (isDirty) {
        if (!window.confirm('変更があります。更新して発行しますか？')) return;
        if (onSaveRef.current) {
          // 2026-09-07: onSave は三方向マージ経由（ReservationEditor.saveViaMerge）。
          //   競合ダイアログで「保存を中止」した場合は例外になるので、発行も止める
          try {
            await onSaveRef.current(safe, { keepOpen: true });
          } catch (e) {
            console.warn('[useSquareInvoice] 保存が中止/失敗したため請求書発行を中止:', e);
            return;
          }
        }
      } else {
        if (!window.confirm('請求書を発行しますか？')) return;
      }

      setIsCreatingInvoice(true);
      try {
        // Required field validation
        if (!safe.contact_code) {
          window.alert('予約番号(contact_code)がありません。');
          setIsCreatingInvoice(false);
          return;
        }
        if (!safe.start_date || !safe.end_date) {
          window.alert('開始日時または終了日時が設定されていません。');
          setIsCreatingInvoice(false);
          return;
        }

        const payload: Record<string, unknown> = buildSquareInvoicePayload(safe, getSquareIndex());

        let res = await fetch(`${SYS_INVOICE_BASE}/square_create_invoice.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        let text = await res.text();
        let data: any;
        // 検算で止まった（明細合計 ≠ 予約の合計）ときは「基本料金だけで一旦請求書を発行しますか？」を出す（2026-11-03）。
        // はい → base_only=true で作り直す / いいえ → 何もしないで戻る。中身と印は lib/invoiceBaseOnly.ts
        let baseOnly: InvoiceSumMismatch | null = null;
        try {
          baseOnly = baseOnlyCandidate(JSON.parse(text));
        } catch {
          baseOnly = null;
        }
        if (baseOnly) {
          if (!window.confirm(buildBaseOnlyConfirmMessage(baseOnly))) {
            setIsCreatingInvoice(false);
            return;
          }
          res = await fetch(`${SYS_INVOICE_BASE}/square_create_invoice.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...payload, base_only: true }),
          });
          text = await res.text();
        }
        try {
          data = JSON.parse(text);
        } catch (jsonError) {
          console.error('[useSquareInvoice] JSON parse error:', jsonError);
          if (!res.ok) {
            window.alert(
              `請求書の発行に失敗しました\n\nHTTPステータス: ${res.status} ${res.statusText}\n` +
                `エラー内容:\n${text.slice(0, 300)}${text.length > 300 ? '...' : ''}`,
            );
            setIsCreatingInvoice(false);
            return;
          }
          throw new Error('Invalid JSON response: ' + text.slice(0, 100));
        }

        if (data.status === 'success' && data.invoice_id) {
          if (baseOnly && data.base_only) {
            // 基本料金だけで作った印を予約の memo に残す（足りない分をあとで追加請求するため）
            onChange?.({
              invoice_link: data.invoice_id,
              memo: appendBaseOnlyMemo(safe.memo, buildBaseOnlyMemoLine(baseOnly, data.invoice_id)),
            });
            window.alert(
              '基本料金だけの請求書を発行しました（題名に「基本料金分」）。\n\n' +
                '予約のメモに印を残しました。足りない分はあとで追加請求してください。',
            );
          } else {
            onChange?.({ invoice_link: data.invoice_id });
            window.alert('請求書を発行しました');
          }

          // Auto-save after onChange propagates
          if (onSaveRef.current) {
            setTimeout(() => {
              const latest = getDataRef.current();
              onSaveRef.current?.(latest, { keepOpen: true });
            }, 300);
          }
        } else {
          const errorMsg =
            typeof data.message === 'string'
              ? data.message
              : JSON.stringify(data.message || 'Unknown error');
          const detailsMsg = data.details
            ? '\n詳細: ' + JSON.stringify(data.details, null, 2)
            : '';
          console.error('[useSquareInvoice] Invoice creation failed:', data);
          window.alert(`請求書の発行に失敗しました\n\nエラー: ${errorMsg}${detailsMsg}`);
        }
      } catch (e: any) {
        console.error('[useSquareInvoice] Create invoice error:', e);
        window.alert(`エラーが発生しました\n\n${e.message}`);
      } finally {
        setIsCreatingInvoice(false);
      }
    },
    [getData, onChange, sysApiBase],
  );

  const voidInvoice = useCallback(async () => {
    if (!isSquareEnabled()) {
      window.alert('この店舗では Square 請求書機能が未設定のため取り消しできません。');
      return;
    }
    const safe = getData();
    const invoiceId = String(safe.invoice_link ?? '').trim();
    if (!invoiceId) return;

    const parsed = parseInvoiceLink(invoiceId);

    // 移行データ由来の決済リンク（https://square.link/u/...）は Square の invoice ID では
    // ないため取り消せない。API に投げると意味の分からないエラーになるので手前で止める。
    if (parsed.isPaymentUrl) {
      window.alert(
        'この予約の請求書リンクは旧形式の決済リンク（square.link）で、Square 請求書として取り消せません。\n\n' +
          'Square 管理画面から直接操作してください。',
      );
      return;
    }

    // 他店舗の Square 請求書は操作させない。宮古の予約には移行データ由来で
    // 那覇 Square の請求書（sq:0:...）が残っており、そのまま取り消すと
    // 那覇の実請求書を消してしまう。
    if (!canOperateSquareIndex(parsed.sqNum)) {
      window.alert(
        `この請求書は他店舗の Square（sqNum=${parsed.sqNum}）で作成されたものです。\n\n` +
          '誤操作防止のため、この画面からは取り消しできません。作成した店舗の画面から操作してください。',
      );
      return;
    }

    if (!window.confirm('この請求書をSquare上で取り消しますか？')) return;

    setIsVoidingInvoice(true);
    try {
      const payload = { invoice_id: invoiceId };
      const res = await fetch(`${SYS_INVOICE_BASE}/square_void_invoice.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const text = await res.text();
      let data: any;
      try {
        data = JSON.parse(text);
      } catch (jsonError) {
        throw new Error('Invalid JSON response: ' + text.slice(0, 100));
      }

      if (data.status === 'success') {
        onChange?.({ invoice_link: '' });
        window.alert('請求書を取り消しました');

        // Auto-save
        if (onSaveRef.current) {
          setTimeout(() => {
            const latest = getDataRef.current();
            onSaveRef.current?.(latest, { keepOpen: true });
          }, 300);
        }
      } else {
        const errorMsg =
          typeof data.message === 'string'
            ? data.message
            : JSON.stringify(data.message || 'Unknown error');
        console.error('[useSquareInvoice] Void invoice failed:', data);
        window.alert(`請求書の取り消しに失敗しました\n\nエラー: ${errorMsg}`);
      }
    } catch (e: any) {
      console.error('[useSquareInvoice] Void invoice error:', e);
      window.alert(`エラーが発生しました\n\n${e.message}`);
    } finally {
      setIsVoidingInvoice(false);
    }
  }, [getData, onChange, sysApiBase]);

  const openInvoiceLink = useCallback(() => {
    const safe = getData();
    const rawValue = String(safe.invoice_link ?? '').trim();
    if (!rawValue) return;

    const { actualId, locationToken, isPaymentUrl } = parseInvoiceLink(rawValue);
    // 旧形式の決済リンク（https://square.link/u/...）は invoice ID ではないので、
    // ダッシュボード URL に埋め込まずそのまま開く（従来は壊れた URL を開いていた）。
    const targetUrl = isPaymentUrl
      ? rawValue
      : `https://app.squareup.com/dashboard/invoices/${actualId}/edit?currentUnitToken=${locationToken}`;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }, [getData]);

  return {
    isCreatingInvoice,
    isVoidingInvoice,
    createInvoice,
    voidInvoice,
    openInvoiceLink,
  };
}
